import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import AnnouncementBar from '@/components/feature/AnnouncementBar';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ProductCard from '@/components/feature/ProductCard';
import FilterSidebar from '@/pages/shop/components/FilterSidebar';
import SortBar from '@/pages/shop/components/SortBar';
import { products } from '@/mocks/products';
import { categories } from '@/mocks/categories';
import { stores, getStoreBySlug } from '@/mocks/stores';

function formatPrice(amount: number): string {
  return `KSh ${amount.toLocaleString('en-KE')}`;
}

function formatNumber(n: number): string {
  if (n >= 1000) {
    return `${(n / 1000).toFixed(1)}k`;
  }
  return String(n);
}

function slugToLabel(slug: string): string {
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function matchesSubSlug(productName: string, productSlug: string, tags: string[], subSlug: string): boolean {
  const subLower = subSlug.toLowerCase();
  const subWords = subLower.split('-');
  const nameLower = productName.toLowerCase();
  const slugLower = productSlug.toLowerCase();
  const tagsLower = tags.map((t) => t.toLowerCase());

  if (slugLower === subLower) return true;
  if (slugLower.includes(subLower)) return true;
  if (nameLower.includes(subLower)) return true;
  if (tagsLower.some((t) => t === subLower)) return true;

  const allWordsMatch = subWords.every((w) => nameLower.includes(w) || tagsLower.some((t) => t.includes(w)));
  return allWordsMatch && subWords.length >= 2;
}

export default function OfficialStore() {
  const { categorySlug, storeSlug } = useParams<{ categorySlug?: string; storeSlug?: string }>();

  const isOfficialStores = categorySlug === 'official-stores';

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sortValue, setSortValue] = useState('relevance');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [inStockOnly, setInStockOnly] = useState(false);

  // --- Store mode data ---
  const store = useMemo(() => {
    if (!isOfficialStores || !storeSlug) return undefined;
    return getStoreBySlug(storeSlug);
  }, [isOfficialStores, storeSlug]);

  const brandName = useMemo(() => {
    if (store) return store.name;
    if (storeSlug) return slugToLabel(storeSlug);
    return 'Official Store';
  }, [store, storeSlug]);

  // --- Subcategory mode data ---
  const category = useMemo(() => {
    if (!categorySlug) return null;
    return categories.find((c) => c.slug === categorySlug) ?? null;
  }, [categorySlug]);

  const subLabel = useMemo(() => {
    if (!storeSlug) return 'All Products';
    return slugToLabel(storeSlug);
  }, [storeSlug]);

  const subSlugItem = useMemo(() => {
    if (!category || !storeSlug) return null;
    for (const group of category.subcategoryGroups || []) {
      const match = group.items.find((item) => item.slug === storeSlug);
      if (match) return match;
    }
    return null;
  }, [category, storeSlug]);

  // --- Products ---
  const filteredProducts = useMemo(() => {
    if (!storeSlug) return [];

    if (isOfficialStores) {
      // Official store: filter by brand
      const brandLower = storeSlug.toLowerCase();
      let result = products.filter((p) => p.brand.toLowerCase() === brandLower);
      return applySort(result, sortValue);
    }

    // Subcategory mode: filter by category + subcategory match
    let result = products;
    if (category) {
      result = result.filter((p) => p.categoryId === category.id);
    }
    result = result.filter((p) =>
      matchesSubSlug(p.name, p.slug, p.tags, storeSlug),
    );

    // Additional filters
    if (selectedBrands.length > 0) {
      result = result.filter((p) => selectedBrands.includes(p.brand));
    }
    const min = priceMin ? parseInt(priceMin, 10) : 0;
    const max = priceMax ? parseInt(priceMax, 10) : Infinity;
    result = result.filter((p) => {
      const price = p.salePrice ?? p.price;
      return price >= min && price <= max;
    });
    if (selectedRating) {
      result = result.filter((p) => p.rating >= selectedRating);
    }
    if (inStockOnly) {
      result = result.filter((p) => p.stock > 0);
    }

    return applySort(result, sortValue);
  }, [storeSlug, isOfficialStores, category, sortValue, selectedBrands, priceMin, priceMax, selectedRating, inStockOnly]);

  const toggleBrand = (brand: string) => {
    setSelectedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand],
    );
  };

  const breadcrumbName = useMemo(() => slugToLabel(categorySlug || 'shop'), [categorySlug]);

  // --- Subcategory Filter Mode (non-official-stores) ---
  if (!isOfficialStores) {
    return (
      <div className="min-h-screen bg-background-50">
        <AnnouncementBar />
        <Navbar />

        <main className="pt-28 md:pt-36 pb-12">
          <div className="w-full px-4 md:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-4">
              <a href="/" className="text-foreground-500 hover:text-primary-600 transition-colors">
                Home
              </a>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs" />
              <a href="/shop" className="text-foreground-500 hover:text-primary-600 transition-colors">
                Shop
              </a>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs" />
              <a href={`/shop/${categorySlug}`} className="text-foreground-500 hover:text-primary-600 transition-colors">
                {breadcrumbName}
              </a>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs" />
              <span className="text-foreground-900 font-medium">{subLabel}</span>
            </nav>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-6">
              {subLabel}
            </h1>

            {/* Mobile filter toggle */}
            <div className="lg:hidden mb-4">
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-800 hover:bg-background-200 transition-colors"
              >
                <i className="ri-filter-3-line" />
                Filters
                {(selectedBrands.length > 0 || priceMin || priceMax || selectedRating || selectedCategory || inStockOnly) && (
                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">
                    {selectedBrands.length + (priceMin || priceMax ? 1 : 0) + (selectedRating ? 1 : 0) + (selectedCategory ? 1 : 0) + (inStockOnly ? 1 : 0)}
                  </span>
                )}
              </button>
            </div>

            <div className="flex gap-6">
              {/* Sidebar - Desktop */}
              <div className="hidden lg:block">
                <div className="sticky top-36">
                  <FilterSidebar
                    selectedBrands={selectedBrands}
                    onBrandToggle={toggleBrand}
                    priceMin={priceMin}
                    priceMax={priceMax}
                    onPriceMinChange={setPriceMin}
                    onPriceMaxChange={setPriceMax}
                    selectedRating={selectedRating}
                    onRatingChange={setSelectedRating}
                    selectedCategory={selectedCategory}
                    onCategoryChange={setSelectedCategory}
                    inStockOnly={inStockOnly}
                    onInStockToggle={() => setInStockOnly((v) => !v)}
                  />
                </div>
              </div>

              {/* Main content */}
              <div className="flex-1 min-w-0">
                <SortBar
                  sortValue={sortValue}
                  onSortChange={setSortValue}
                  resultCount={filteredProducts.length}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                />

                {filteredProducts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                      <i className="ri-search-line text-2xl text-foreground-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground-800 mb-1">No products found</h3>
                    <p className="text-sm text-foreground-500">Try adjusting your filters or browse the broader category.</p>
                  </div>
                ) : (
                  <div
                    className={
                      viewMode === 'grid'
                        ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4 pt-4'
                        : 'flex flex-col gap-3 pt-4'
                    }
                  >
                    {filteredProducts.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>

        {/* Mobile Filter Panel */}
        {mobileFiltersOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-foreground-950/50"
              onClick={() => setMobileFiltersOpen(false)}
            />
            <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-background-50 shadow-xl overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b border-background-200/60">
                <h3 className="text-base font-bold text-foreground-950">Filters</h3>
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors"
                >
                  <i className="ri-close-line text-lg text-foreground-700" />
                </button>
              </div>
              <div className="p-4">
                <FilterSidebar
                  selectedBrands={selectedBrands}
                  onBrandToggle={toggleBrand}
                  priceMin={priceMin}
                  priceMax={priceMax}
                  onPriceMinChange={setPriceMin}
                  onPriceMaxChange={setPriceMax}
                  selectedRating={selectedRating}
                  onRatingChange={setSelectedRating}
                  selectedCategory={selectedCategory}
                  onCategoryChange={setSelectedCategory}
                  inStockOnly={inStockOnly}
                  onInStockToggle={() => setInStockOnly((v) => !v)}
                />
              </div>
              <div className="p-4 border-t border-background-200/60">
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(false)}
                  className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors"
                >
                  Show {filteredProducts.length} Results
                </button>
              </div>
            </div>
          </div>
        )}

        <Footer />
      </div>
    );
  }

  // ===== OFFICIAL STORE MODE =====
  return (
    <div className="min-h-screen bg-background-50">
      <AnnouncementBar />
      <Navbar />

      <main className="pt-28 md:pt-36">
        {/* Store Hero Banner */}
        <div className="relative w-full h-52 md:h-64 lg:h-72 overflow-hidden bg-background-100">
          {store?.banner ? (
            <img
              src={store.banner}
              alt={`${brandName} Official Store`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-r from-background-200 via-background-100 to-background-200" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/10 via-foreground-950/5 to-foreground-950/30" />
        </div>

        {/* Store Info Bar */}
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="relative -mt-16 md:-mt-20 z-10">
            <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-6">
              {/* Store Logo */}
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-xl bg-background-50 border-4 border-background-50 shadow-sm overflow-hidden flex-shrink-0">
                {store?.logo ? (
                  <img
                    src={store.logo}
                    alt={brandName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-background-200 flex items-center justify-center">
                    <span className="text-2xl font-bold text-foreground-500">{brandName.charAt(0)}</span>
                  </div>
                )}
              </div>

              {/* Store Info */}
              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                  {brandName} Official Store
                </h1>
                {store && (
                  <div className="flex flex-wrap items-center gap-3 mt-1.5">
                    <span className="text-sm text-foreground-600">
                      <span className="font-semibold text-foreground-800">{formatNumber(store.followers)}</span> Followers
                    </span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300" />
                    <span className="text-sm text-foreground-600">
                      <span className="font-semibold text-foreground-800">{store.totalProducts}+</span> Products
                    </span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300" />
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <i
                          key={s}
                          className={`ri-star-fill text-xs ${s <= Math.round(store.rating) ? 'text-accent-500' : 'text-background-300'}`}
                        />
                      ))}
                      <span className="text-sm font-semibold text-foreground-800 ml-0.5">{store.rating}</span>
                    </div>
                    <span className="w-1 h-1 rounded-full bg-foreground-300" />
                    <span className="text-sm text-foreground-600">
                      Joined {store.joinedYear}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                    <i className="ri-shield-check-line text-xs" />
                    Official Store
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                    <i className="ri-truck-line text-xs" />
                    Fast Delivery
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                    <i className="ri-refresh-line text-xs" />
                    Free Returns
                  </span>
                </div>
              </div>

              {/* Follow Button */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  className="h-10 px-6 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-add-line mr-1.5" />
                  Follow
                </button>
                <button
                  type="button"
                  className="w-10 h-10 rounded-full border border-background-200/70 flex items-center justify-center text-foreground-600 hover:bg-background-100 transition-colors"
                  aria-label="Share store"
                >
                  <i className="ri-share-line" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 lg:px-8 mt-6">
          <nav className="flex items-center gap-2 text-sm">
            <a href="/" className="text-foreground-500 hover:text-primary-600 transition-colors">
              Home
            </a>
            <i className="ri-arrow-right-s-line text-foreground-400 text-xs" />
            <a href="/shop" className="text-foreground-500 hover:text-primary-600 transition-colors">
              Shop
            </a>
            <i className="ri-arrow-right-s-line text-foreground-400 text-xs" />
            <a href={`/shop/${categorySlug}`} className="text-foreground-500 hover:text-primary-600 transition-colors">
              {breadcrumbName}
            </a>
            <i className="ri-arrow-right-s-line text-foreground-400 text-xs" />
            <span className="text-foreground-900 font-medium">{brandName}</span>
          </nav>
        </div>

        {/* Store Description */}
        {store?.description && (
          <div className="w-full px-4 md:px-6 lg:px-8 mt-6">
            <div className="bg-background-100 rounded-xl p-4 md:p-5 border border-background-200/60">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-store-2-line text-primary-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">About {brandName} Official Store</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{store.description}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Products Section */}
        <div className="w-full px-4 md:px-6 lg:px-8 mt-8 pb-12">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading">
                All Products
              </h2>
              <p className="text-sm text-foreground-500 mt-0.5">
                <span className="font-semibold text-foreground-800">{filteredProducts.length}</span> products from {brandName}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <label htmlFor="store-sort" className="text-sm text-foreground-600 hidden sm:inline">
                  Sort by:
                </label>
                <select
                  id="store-sort"
                  value={sortValue}
                  onChange={(e) => setSortValue(e.target.value)}
                  className="h-9 px-3 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
                >
                  <option value="relevance">Relevance</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                  <option value="rating">Top Rated</option>
                  <option value="newest">Newest</option>
                  <option value="bestselling">Best Selling</option>
                </select>
              </div>
              <div className="flex items-center rounded-lg border border-background-200/70 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setViewMode('grid')}
                  className={`w-9 h-9 flex items-center justify-center transition-colors ${
                    viewMode === 'grid' ? 'bg-primary-500 text-background-50' : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                  aria-label="Grid view"
                >
                  <i className="ri-grid-fill" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={`w-9 h-9 flex items-center justify-center transition-colors ${
                    viewMode === 'list' ? 'bg-primary-500 text-background-50' : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                  aria-label="List view"
                >
                  <i className="ri-list-check" />
                </button>
              </div>
            </div>
          </div>

          {filteredProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-store-2-line text-2xl text-foreground-400" />
              </div>
              <h3 className="text-lg font-semibold text-foreground-800 mb-1">No products yet</h3>
              <p className="text-sm text-foreground-500">This store hasn&apos;t listed any products yet. Check back soon!</p>
            </div>
          ) : (
            <div
              className={
                viewMode === 'grid'
                  ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4'
                  : 'flex flex-col gap-3'
              }
            >
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

function applySort(result: typeof products, sortValue: string) {
  const sorted = [...result];
  switch (sortValue) {
    case 'price_asc':
      sorted.sort((a, b) => (a.salePrice ?? a.price) - (b.salePrice ?? b.price));
      break;
    case 'price_desc':
      sorted.sort((a, b) => (b.salePrice ?? b.price) - (a.salePrice ?? a.price));
      break;
    case 'rating':
      sorted.sort((a, b) => b.rating - a.rating);
      break;
    case 'newest':
      sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      break;
    case 'bestselling':
      sorted.sort((a, b) => b.soldCount - a.soldCount);
      break;
    default:
      break;
  }
  return sorted;
}